import {
  GOFO_BYOCA_ORIGIN_HUBS,
  GofoByocaOriginHub,
} from "@redotech/redo-fulfillment-carrier-common/gofo-hubs";
import { useTranslation } from "@redotech/redo-i18n";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { nonEmptyValidator } from "@redotech/ui/validators";
import { memo } from "react";
import { RedoTextInput } from "../../../../arbiter-components/input/redo-text-input";
import { RedoExternalLink } from "../../../../arbiter-components/links/redo-external-link";
import { RedoListItem } from "../../../../arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "../../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "../../../../flex";
import { Text } from "../../../../text";

export const GofoCarrierInput = groupInput({
  username: input<string>({ validator: nonEmptyValidator }),
  password: input<string>({ validator: nonEmptyValidator }),
  originHub: input<GofoByocaOriginHub | undefined>({
    validator: nonEmptyValidator,
  }),
});

export type GofoCarrierValue = InputSchema.Value<typeof GofoCarrierInput>;

export type GofoCarrierForm = InputSchema.Input<typeof GofoCarrierInput>;

export const gofoCarrierDefault = (): GofoCarrierValue => ({
  username: "",
  password: "",
  originHub: undefined,
});

const originHubOptions: RedoListItem<GofoByocaOriginHub>[] =
  GOFO_BYOCA_ORIGIN_HUBS.map((hub) => ({
    id: hub.code,
    type: "text",
    text: `${hub.code} - ${hub.label}`,
    value: hub.code,
  }));

interface CreateGofoCarrierFormProps {
  carrierInput: GofoCarrierForm;
}

export const CreateGofoCarrierForm = memo(function CreateGofoCarrierForm({
  carrierInput,
}: CreateGofoCarrierFormProps) {
  const { t } = useTranslation();

  return (
    <>
      <Flex align="flex-start" flexDirection="column" gap="xs" mb="lg">
        <Text fontSize="xs" textColor="secondary">
          {t(
            "Use shipper credentials issued by GOFO and ask GOFO to enable EasyPost label purchases. Negotiated rates are available in production mode. Select the supported origin hub closest to your shipment origin address.",
          )}
        </Text>
        <Text fontSize="xs" textColor="secondary">
          {t(
            "GOFO Parcel ships within the US, up to 25 lb and 30 inches in length. Returns, hazardous materials, signatures, and manifests are not supported. Arrange pickups directly with GOFO.",
          )}
        </Text>
        <RedoExternalLink
          text={t("GOFO account setup and supported services")}
          url="https://docs.easypost.com/carriers/gofo-byoca-guide"
        />
      </Flex>
      <RedoTextInput
        label={t("Username")}
        placeholder=""
        setValue={carrierInput.inputs.username.setValue}
        value={carrierInput.inputs.username.value}
      />
      <RedoTextInput
        inputTypeHint="password"
        label={t("Password")}
        placeholder=""
        setValue={carrierInput.inputs.password.setValue}
        value={carrierInput.inputs.password.value}
      />
      <RedoSingleSelectDropdownInput
        label={t("Origin hub")}
        options={originHubOptions}
        optionSelected={({
          item,
        }: {
          item: RedoListItem<GofoByocaOriginHub>;
        }) => {
          carrierInput.inputs.originHub.setValue(item.value);
        }}
        selectedItem={originHubOptions.find(
          (option) => option.value === carrierInput.inputs.originHub.value,
        )}
      />
    </>
  );
});
